import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — the ESPR working plan 2025-2030 puts aluminium (an
 * intermediate product) in the ~2027 delegated-act wave. Derived from the
 * metals-family reference (steel-v1) with aluminium-specific accents: alloy
 * series (EN 573 / xxxx designations), temper, and — critically — the smelting
 * energy source, because primary aluminium's footprint is dominated by
 * electricity (hydro vs coal grid swings embedded CO2e by an order of
 * magnitude). Aluminium is a CBAM good, so embedded carbon doubles as the CBAM
 * figure. Field ids are STABLE; the list is trued up when the delegated act
 * lands by adding fields / flipping `required`/`tier`, never by renaming ids.
 */
export const aluminiumV1: PassportSchemaDef = {
  id: "aluminium-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Alüminyum Ürün Pasaportu",
    en: "Aluminium Product Passport",
    de: "Aluminium-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/aluminium/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.aluminiumType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["primary", "secondary-recycled", "wrought", "cast", "other"],
          labels: { tr: "Alüminyum türü", en: "Aluminium type", de: "Aluminiumtyp" },
        },
        {
          id: "identity.productForm",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "coil",
            "sheet",
            "plate",
            "extrusion-profile",
            "foil",
            "billet",
            "ingot",
            "wire",
            "casting",
            "other",
          ],
          labels: { tr: "Ürün formu", en: "Product form", de: "Produktform" },
        },
        {
          id: "identity.alloyDesignation",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Alaşım tanımı", en: "Alloy designation", de: "Legierungsbezeichnung" },
          description: "EN 573 designation, e.g. EN AW-6060.",
          highlight: true,
        },
        {
          id: "identity.temper",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Temper (malzeme durumu)", en: "Temper", de: "Zustand (Temper)" },
          description: "E.g. T6, H14.",
        },
        {
          id: "identity.brand",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
          highlight: true,
        },
        {
          id: "identity.smelterName",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Ergitme/üretim tesisi",
            en: "Smelter / producing plant",
            de: "Hütte / Herstellwerk",
          },
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Döküm/parti numarası", en: "Cast/batch number", de: "Guss-/Chargennummer" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "composition",
      labels: { tr: "Bileşim", en: "Composition", de: "Zusammensetzung" },
      fields: [
        {
          id: "composition.alloySeries",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["1xxx", "2xxx", "3xxx", "4xxx", "5xxx", "6xxx", "7xxx", "8xxx", "cast", "other"],
          labels: { tr: "Alaşım serisi", en: "Alloy series", de: "Legierungsserie" },
        },
        {
          id: "composition.alloyingElements",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Alaşım elementleri", en: "Alloying elements", de: "Legierungselemente" },
          description: "E.g. Mg, Si, Cu, Zn.",
        },
        {
          id: "composition.coatingType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["none", "anodized", "powder-coated", "painted", "mill-finish", "lacquered", "other"],
          labels: { tr: "Kaplama türü", en: "Coating type", de: "Beschichtungstyp" },
        },
        {
          id: "composition.surfaceTreatment",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Yüzey işlemi", en: "Surface treatment", de: "Oberflächenbehandlung" },
        },
        {
          id: "composition.hazardousSubstancesPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Tehlikeli madde içerir",
            en: "Contains hazardous substances",
            de: "Enthält gefährliche Stoffe",
          },
        },
        {
          id: "composition.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
          description: "REACH candidate-list substances above 0.1% by weight.",
        },
      ],
    },
    {
      id: "sustainability",
      labels: { tr: "Sürdürülebilirlik", en: "Sustainability", de: "Nachhaltigkeit" },
      fields: [
        {
          id: "sustainability.recycledContentPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: { tr: "Geri dönüştürülmüş içerik", en: "Recycled content", de: "Rezyklatanteil" },
          highlight: true,
        },
        {
          id: "sustainability.postConsumerScrapPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Tüketici sonrası hurda oranı",
            en: "Post-consumer scrap",
            de: "Post-Consumer-Schrottanteil",
          },
        },
        {
          id: "sustainability.preConsumerScrapPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Tüketici öncesi hurda oranı",
            en: "Pre-consumer scrap",
            de: "Pre-Consumer-Schrottanteil",
          },
        },
        {
          id: "sustainability.productionRoute",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["primary-smelting", "secondary-remelting", "mixed", "other"],
          labels: { tr: "Üretim rotası", en: "Production route", de: "Produktionsroute" },
        },
        {
          id: "sustainability.smeltingEnergySource",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["hydro", "renewable-mix", "grid-mix", "coal", "natural-gas", "other"],
          labels: {
            tr: "Ergitme enerji kaynağı",
            en: "Smelting energy source",
            de: "Energiequelle der Schmelze",
          },
          description: "Dominant driver of primary aluminium's carbon footprint.",
        },
        {
          id: "sustainability.embeddedCarbonKgCo2PerTonne",
          type: "number",
          required: true,
          tier: "public",
          unit: "kgCO2e/t",
          labels: {
            tr: "Gömülü emisyon (ton başına)",
            en: "Embedded emissions (per tonne)",
            de: "Eingebettete Emissionen (pro Tonne)",
          },
          description: "Cradle-to-gate embedded CO2e per tonne — shared with the CBAM declaration.",
          highlight: true,
        },
        {
          id: "sustainability.carbonFootprintMethod",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["en-15804", "iso-14067", "ia-aluminium-lci", "pef", "other"],
          labels: {
            tr: "Karbon ayak izi yöntemi",
            en: "Carbon footprint method",
            de: "Methode des CO2-Fußabdrucks",
          },
        },
        {
          id: "sustainability.cbamReported",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "CBAM raporlandı", en: "CBAM reported", de: "CBAM gemeldet" },
        },
        {
          id: "sustainability.cbamCnCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "CBAM CN kodu", en: "CBAM CN code", de: "CBAM-KN-Code" },
        },
      ],
    },
    {
      id: "performance",
      labels: { tr: "Performans", en: "Performance", de: "Leistung" },
      fields: [
        {
          id: "performance.standardConformity",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Standart uygunluğu", en: "Standard conformity", de: "Normkonformität" },
          description: "Product standard conformed to, e.g. EN 485 / EN 755.",
        },
        {
          id: "performance.temperDetail",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Temper detayı", en: "Temper detail", de: "Zustandsdetail" },
        },
        {
          id: "performance.yieldStrengthMpa",
          type: "number",
          required: false,
          tier: "public",
          unit: "MPa",
          labels: { tr: "Akma dayanımı", en: "Yield strength", de: "Streckgrenze" },
        },
        {
          id: "performance.tensileStrengthMpa",
          type: "number",
          required: false,
          tier: "public",
          unit: "MPa",
          labels: { tr: "Çekme dayanımı", en: "Tensile strength", de: "Zugfestigkeit" },
        },
        {
          id: "performance.elongationPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Kopma uzaması", en: "Elongation", de: "Bruchdehnung" },
        },
        {
          id: "performance.hardness",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Sertlik", en: "Hardness", de: "Härte" },
        },
        {
          id: "performance.millTestCertificateUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Muayene sertifikası (EN 10204)",
            en: "Mill test certificate (EN 10204)",
            de: "Abnahmeprüfzeugnis (EN 10204)",
          },
        },
        {
          id: "performance.intendedUse",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Kullanım amacı", en: "Intended use", de: "Verwendungszweck" },
        },
      ],
    },
    {
      id: "circularity",
      labels: { tr: "Döngüsellik", en: "Circularity", de: "Kreislauffähigkeit" },
      fields: [
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
          highlight: true,
        },
        {
          id: "circularity.recyclabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülebilirlik oranı",
            en: "Recyclability rate",
            de: "Recyclingfähigkeitsquote",
          },
        },
        {
          id: "circularity.reusePotential",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Yeniden kullanım potansiyeli",
            en: "Reuse potential",
            de: "Wiederverwendungspotenzial",
          },
        },
        {
          id: "circularity.endOfLifeGuidance",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu yönlendirmesi",
            en: "End-of-life guidance",
            de: "Hinweise zum Lebensende",
          },
        },
        {
          id: "circularity.scrapSeparationGuidance",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Hurda ayrıştırma rehberi",
            en: "Scrap separation guidance",
            de: "Anleitung zur Schrotttrennung",
          },
        },
      ],
    },
  ],
};
